import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Upload, ExternalLink, Copy, Plus, Trash2, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { agencyPublicUrl, PUBLIC_AGENCY_BASE_URL } from '../../lib/utils';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import recruitmentApi from '../../utils/agencyRecruitmentApi';

/**
 * Iter-228 — Agency-side editor for the branded recruitment page.
 * Surfaces every field on agency_recruitment_pages plus uploads.
 */
const RecruitmentSettings = () => {
  const [page, setPage] = useState(null);
  const [counters, setCounters] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const logoRef = useRef(null);
  const bannerRef = useRef(null);
  const brandRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([recruitmentApi.get(), recruitmentApi.counters()]);
      setPage(p.data);
      setCounters(c.data);
    } catch (e) {
      toast.error('Failed to load recruitment settings');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = (patch) => setPage((p) => ({ ...p, ...patch }));

  const save = async (extraPatch = {}) => {
    setSaving(true);
    try {
      const body = { ...page, ...extraPatch };
      const res = await recruitmentApi.update(body);
      setPage(res.data);
      toast.success('Saved');
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
      return null;
    } finally { setSaving(false); }
  };

  const upload = async (kind, fileInputRef) => {
    const f = fileInputRef.current?.files?.[0];
    if (!f) return;
    setSaving(true);
    try {
      const res = await recruitmentApi.uploadAsset(kind, f);
      const url = res.data?.url;
      if (!url) throw new Error('upload-no-url');
      if (kind === 'logo') await save({ logo_url: url });
      else if (kind === 'banner') await save({ banner_url: url });
      else if (kind === 'brand-logo') {
        const brands = Array.isArray(page.brands_worked_with) ? [...page.brands_worked_with] : [];
        brands.push({ logo_url: url, name: '' });
        await save({ brands_worked_with: brands });
      }
      fileInputRef.current.value = '';
    } catch (e) {
      toast.error('Upload failed');
    } finally { setSaving(false); }
  };

  const togglePublish = async () => {
    if (page.is_published) {
      await save({ is_published: false });
    } else {
      const res = await save({ is_published: true });
      if (res?.is_published) toast.success('Your recruitment page is live!');
    }
  };

  const copyLink = () => {
    const url = agencyPublicUrl(page.slug);
    navigator.clipboard.writeText(url);
    toast.success('Link copied · ' + url);
  };

  if (loading) {
    return <div className="p-12 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-cyan-600" /></div>;
  }
  if (!page) return null;

  const publicUrl = agencyPublicUrl(page.slug);

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6" data-testid="recruitment-settings-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Recruitment Page</h1>
          <p className="text-sm text-slate-600 mt-1">Brand your public agency page and control how talent joins your team.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-cyan-700 hover:underline" data-testid="preview-link">
            <Eye className="w-4 h-4" /> Preview
          </a>
          <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-link-btn">
            <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
          </Button>
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
            <span className="text-sm text-slate-700">{page.is_published ? 'Published' : 'Draft'}</span>
            <Switch checked={!!page.is_published} onCheckedChange={togglePublish} data-testid="publish-toggle" />
          </div>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3" data-testid="recruitment-counters">
        {[
          ['Page visits (30d)', counters?.page_visits_30d ?? 0, 'counter-visits'],
          ['Registrations (30d)', counters?.registrations_30d ?? 0, 'counter-regs'],
          ['Joins (30d)', counters?.joins_30d ?? 0, 'counter-joins'],
          ['Active roster', counters?.active_roster_size ?? 0, 'counter-active'],
          ['Pending approval', counters?.pending_approval_count ?? 0, 'counter-pending'],
        ].map(([label, val, testid]) => (
          <div key={testid} className="bg-white border border-slate-100 rounded-xl px-3 py-2 text-center" data-testid={testid}>
            <div className="text-xl font-bold text-slate-900">{val}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* URL + slug */}
      <Card>
        <CardHeader><CardTitle>Your link</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 font-mono">{PUBLIC_AGENCY_BASE_URL.replace(/^https?:\/\//, '')}/agency/</span>
            <Input
              value={page.slug || ''}
              onChange={(e) => update({ slug: e.target.value.toLowerCase() })}
              onBlur={() => save({ slug: page.slug })}
              className="max-w-xs"
              data-testid="slug-input"
            />
          </div>
          <p className="text-xs text-slate-500">
            Your public link: <span className="font-mono text-slate-700">{publicUrl}</span>. Changing the slug preserves old links via automatic redirects.
          </p>
        </CardContent>
      </Card>

      {/* Approval mode */}
      <Card>
        <CardHeader><CardTitle>Who can join?</CardTitle></CardHeader>
        <CardContent>
          <Select value={page.approval_mode || ''} onValueChange={(v) => save({ approval_mode: v })}>
            <SelectTrigger data-testid="approval-mode-select"><SelectValue placeholder="Choose a mode" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto_accept">Auto-accept — anyone joining lands on the roster instantly</SelectItem>
              <SelectItem value="approval_required">Approval required — review each application before adding</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-2">
            You must pick a mode before you can publish.
          </p>
        </CardContent>
      </Card>

      {/* Branding */}
      <Card>
        <CardHeader><CardTitle>Branding</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label>Logo</Label>
            <div className="flex items-center gap-3 mt-1">
              {page.logo_url && <img src={page.logo_url} alt="Logo" className="w-16 h-16 rounded-lg object-cover border" />}
              <input ref={logoRef} type="file" accept="image/*" onChange={() => upload('logo', logoRef)} className="text-xs" data-testid="upload-logo-input" />
            </div>
          </div>
          <div>
            <Label>Banner image</Label>
            <div className="flex items-center gap-3 mt-1">
              {page.banner_url && <img src={page.banner_url} alt="Banner" className="h-16 w-32 rounded-lg object-cover border" />}
              <input ref={bannerRef} type="file" accept="image/*" onChange={() => upload('banner', bannerRef)} className="text-xs" data-testid="upload-banner-input" />
            </div>
          </div>
          <div>
            <Label>Tagline (max 140 chars)</Label>
            <Input maxLength={140} value={page.tagline || ''} onChange={(e) => update({ tagline: e.target.value })} onBlur={() => save({ tagline: page.tagline })} data-testid="tagline-input" />
          </div>
        </CardContent>
      </Card>

      {/* Content body */}
      <Card>
        <CardHeader><CardTitle>Page content</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Why join us</Label>
            <Textarea rows={4} value={page.why_join_us || ''} onChange={(e) => update({ why_join_us: e.target.value })} onBlur={() => save({ why_join_us: page.why_join_us })} data-testid="why-join-input" />
          </div>
          <div>
            <Label>Agency description</Label>
            <Textarea rows={5} value={page.description || ''} onChange={(e) => update({ description: e.target.value })} onBlur={() => save({ description: page.description })} data-testid="description-input" />
          </div>
          <div>
            <Label>Recruiting message</Label>
            <Textarea rows={3} value={page.recruiting_message || ''} onChange={(e) => update({ recruiting_message: e.target.value })} onBlur={() => save({ recruiting_message: page.recruiting_message })} data-testid="recruit-msg-input" />
          </div>
        </CardContent>
      </Card>

      {/* Markets + Roles */}
      <Card>
        <CardHeader><CardTitle>Hiring focus</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-5">
          <ChipEditor
            label="Hiring markets"
            placeholder="e.g. Atlanta, GA"
            value={page.hiring_markets || []}
            onChange={(v) => save({ hiring_markets: v })}
            testIdPrefix="market"
          />
          <ChipEditor
            label="Hiring roles"
            placeholder="e.g. Brand Ambassador"
            value={page.hiring_roles || []}
            onChange={(v) => save({ hiring_roles: v })}
            testIdPrefix="role"
          />
        </CardContent>
      </Card>

      {/* Brands worked with */}
      <Card>
        <CardHeader><CardTitle>Brands you&apos;ve worked with</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
            {(page.brands_worked_with || []).map((b, i) => (
              <div key={i} className="relative bg-slate-50 rounded-lg p-2 text-center" data-testid={`brand-card-${i}`}>
                <button onClick={async () => {
                  const copy = [...page.brands_worked_with]; copy.splice(i, 1);
                  await save({ brands_worked_with: copy });
                }} className="absolute -top-1.5 -right-1.5 bg-rose-100 text-rose-700 rounded-full p-1" data-testid={`brand-remove-${i}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
                {b.logo_url ? (
                  <img src={b.logo_url} alt={b.name || ''} className="h-12 w-full object-contain" />
                ) : (
                  <div className="h-12 flex items-center justify-center text-xs text-slate-400">no logo</div>
                )}
                <Input className="mt-1 h-7 text-xs" placeholder="Brand name" value={b.name || ''} onChange={(e) => {
                  const copy = [...page.brands_worked_with]; copy[i] = { ...copy[i], name: e.target.value };
                  update({ brands_worked_with: copy });
                }} onBlur={() => save({ brands_worked_with: page.brands_worked_with })} />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input ref={brandRef} type="file" accept="image/*" onChange={() => upload('brand-logo', brandRef)} className="text-xs" data-testid="upload-brand-input" />
            <span className="text-xs text-slate-500">PNG/SVG with transparent background works best.</span>
          </div>
        </CardContent>
      </Card>

      {/* Requirements */}
      <Card>
        <CardHeader><CardTitle>Onboarding requirements</CardTitle></CardHeader>
        <CardContent>
          <RequirementsEditor
            value={page.agency_requirements || []}
            onChange={(v) => save({ agency_requirements: v })}
          />
        </CardContent>
      </Card>

      {/* Contact + socials */}
      <Card>
        <CardHeader><CardTitle>Contact &amp; socials</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Recruiting email</Label>
            <Input value={page.contact_info?.email || ''} onChange={(e) => update({ contact_info: { ...page.contact_info, email: e.target.value } })} onBlur={() => save({ contact_info: page.contact_info })} data-testid="contact-email-input" />
          </div>
          <div>
            <Label>Recruiting phone</Label>
            <Input value={page.contact_info?.phone || ''} onChange={(e) => update({ contact_info: { ...page.contact_info, phone: e.target.value } })} onBlur={() => save({ contact_info: page.contact_info })} data-testid="contact-phone-input" />
          </div>
          <div>
            <Label>Website</Label>
            <Input value={page.website_url || ''} onChange={(e) => update({ website_url: e.target.value })} onBlur={() => save({ website_url: page.website_url })} data-testid="website-input" />
          </div>
          <div>
            <Label>Instagram handle</Label>
            <Input value={page.instagram_handle || ''} onChange={(e) => update({ instagram_handle: e.target.value })} onBlur={() => save({ instagram_handle: page.instagram_handle })} data-testid="ig-input" />
          </div>
        </CardContent>
      </Card>

      {saving && <div className="fixed bottom-4 right-4 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-full">Saving…</div>}
    </div>
  );
};


const ChipEditor = ({ label, placeholder, value, onChange, testIdPrefix }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim(); if (!v) return;
    if (value.includes(v)) return setDraft('');
    onChange([...value, v]); setDraft('');
  };
  const remove = (i) => { const copy = [...value]; copy.splice(i, 1); onChange(copy); };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-1.5 mt-2 mb-2">
        {value.map((v, i) => (
          <Badge key={i} className="bg-slate-100 text-slate-800 hover:bg-slate-200 cursor-pointer" onClick={() => remove(i)} data-testid={`${testIdPrefix}-chip-${i}`}>
            {v} <span className="ml-1 text-slate-500">×</span>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input placeholder={placeholder} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} data-testid={`${testIdPrefix}-input`} />
        <Button type="button" onClick={add} variant="outline" data-testid={`${testIdPrefix}-add-btn`}><Plus className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
};


const RequirementsEditor = ({ value, onChange }) => {
  const updateAt = (i, patch) => { const copy = [...value]; copy[i] = { ...copy[i], ...patch }; onChange(copy); };
  const remove = (i) => { const copy = [...value]; copy.splice(i, 1); onChange(copy); };
  const add = () => onChange([...value, { label: '', detail: '' }]);
  return (
    <div className="space-y-2">
      {value.map((req, i) => (
        <div key={i} className="flex gap-2" data-testid={`req-row-${i}`}>
          <Input className="max-w-[180px]" placeholder="Label (e.g. W-9)" value={req.label} onChange={(e) => updateAt(i, { label: e.target.value })} onBlur={() => onChange(value)} />
          <Input placeholder="Detail (e.g. Required before first shift)" value={req.detail} onChange={(e) => updateAt(i, { detail: e.target.value })} onBlur={() => onChange(value)} />
          <Button type="button" variant="outline" onClick={() => remove(i)} data-testid={`req-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} data-testid="req-add"><Plus className="w-3.5 h-3.5 mr-1" /> Add requirement</Button>
    </div>
  );
};

export default RecruitmentSettings;
